import React, { useState, useEffect } from 'react';
import {
  Table, Button, Space, Tag, Spin, message, Typography, 
  Tooltip, Empty, Card, Popconfirm,
} from 'antd';
import { 
  FileTextOutlined, EyeOutlined, ReloadOutlined,
  PrinterOutlined, DownloadOutlined, DeleteOutlined, PlusOutlined,
} from '@ant-design/icons';
import { 
  getSettlementPaymentVouchers, 
  deleteSettlementPaymentVoucher,
  regenerateSettlementPaymentVoucher,
  getSettlementPaymentVoucher,
  fetchSettlementPayments,
  createSettlementPaymentVoucher,
} from '../api';
import VoucherDetailsModal from './VoucherDetailsModal';
import { SplitReferenceCell, resolveSplits } from '../../../components/PaymentSplitCells';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const SettlementPaymentVoucherTable = ({
  caseId,
  caseData,
  refreshTrigger,
  onVoucherUpdate,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  
  const [vouchers, setVouchers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingVouchers, setGeneratingVouchers] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedVoucherId, setSelectedVoucherId] = useState(null);
  const [deletingVoucherId, setDeletingVoucherId] = useState(null);
  const [regeneratingVoucherId, setRegeneratingVoucherId] = useState(null);

  // Load settlement payments
  const loadPayments = async () => {
    if (!caseId) return;
    try {
      const result = await fetchSettlementPayments(caseId, { limit: 50 });
      setPayments(result.data || []);
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error('Load settlement payments error:', error);
      } else {
        setPayments([]);
      }
    }
  };

  // Load settlement payment vouchers
  const loadVouchers = async () => {
    if (!caseId) return;
    
    setLoading(true);
    try {
      const result = await getSettlementPaymentVouchers(caseId, { limit: 50 });
      setVouchers(result.data?.vouchers || []);
      await loadPayments();
    } catch (error) {
      // 404 is expected if no vouchers exist
      if (error?.response?.status !== 404) {
        console.error('Load settlement vouchers error:', error);
        messageApi.error('Failed to load settlement vouchers');
      } else {
        setVouchers([]);
        await loadPayments();
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVouchers();
  }, [caseId, refreshTrigger]);

  // Handle view voucher
  const handleViewVoucher = (voucherId) => {
    setSelectedVoucherId(voucherId);
    setDetailsModalOpen(true);
  };

  // Handle delete voucher
  const handleDeleteVoucher = async (voucherId) => {
    setDeletingVoucherId(voucherId);
    try {
      await deleteSettlementPaymentVoucher(voucherId);
      messageApi.success('Settlement payment voucher deleted');
      await loadVouchers();
      onVoucherUpdate?.();
    } catch (error) {
      console.error('Delete settlement voucher error:', error);
      messageApi.error(error?.response?.data?.message || 'Failed to delete voucher');
    } finally {
      setDeletingVoucherId(null);
    }
  };

  // Handle regenerate voucher
  const handleRegenerateVoucher = async (voucherId) => {
    setRegeneratingVoucherId(voucherId);
    try {
      await regenerateSettlementPaymentVoucher(voucherId, 'Regenerated from Settlement tab');
      messageApi.success('Settlement payment voucher regenerated');
      await loadVouchers();
      onVoucherUpdate?.();
    } catch (error) {
      console.error('Regenerate settlement voucher error:', error);
      messageApi.error(error?.response?.data?.message || 'Failed to regenerate voucher');
    } finally {
      setRegeneratingVoucherId(null);
    }
  };

  // Handle quick print
  const handleQuickPrint = (pdfUrl) => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    } else {
      messageApi.warning('PDF URL not available');
    }
  };

  // Handle quick download
  const handleQuickDownload = (pdfUrl, voucherId) => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `settlement-voucher-${voucherId}.pdf`;
      link.click();
    } else {
      messageApi.warning('PDF URL not available');
    }
  };

  // Get settlement payments that don't have vouchers yet
  const getMissingVouchers = () => {
    const voucherPaymentIds = vouchers.map(v => v.paymentId);
    return payments.filter(p => !voucherPaymentIds.includes(p.paymentId));
  };

  // Handle generate vouchers for missing settlement payments
  const handleGenerateMissingVouchers = async () => {
    const missingPayments = getMissingVouchers();
    
    if (missingPayments.length === 0) {
      messageApi.info('All settlement payments have vouchers');
      return;
    }

    setGeneratingVouchers(true);
    let successCount = 0;
    let failureCount = 0;

    try {
      for (const payment of missingPayments) {
        try {
          await createSettlementPaymentVoucher(payment._id || payment.paymentId);
          successCount++;
        } catch (err) {
          console.error(`Failed to generate voucher for payment ${payment.paymentId}:`, err);
          failureCount++;
        }
      }

      if (successCount > 0) {
        messageApi.success(`Generated ${successCount} voucher${successCount > 1 ? 's' : ''}`);
        await loadVouchers();
        onVoucherUpdate?.();
      }

      if (failureCount > 0) {
        messageApi.warning(`Failed to generate ${failureCount} voucher${failureCount > 1 ? 's' : ''}`);
      }
    } catch (error) {
      console.error('Generate vouchers error:', error);
      messageApi.error('Failed to generate vouchers');
    } finally {
      setGeneratingVouchers(false);
    }
  };

  // Format amount
  const formatWholeAmount = (price) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price || 0);

  // A voucher stores only the collapsed mode — 'SPLIT' when the payment was made
  // across several modes. The breakdown lives on the SettlementPayment, which the
  // list endpoint now joins in; fall back to the separately loaded payments so
  // the columns still fill in against an older backend.
  const paymentByPaymentId = React.useMemo(() => {
    const map = {};
    payments.forEach((p) => {
      if (p.paymentId) map[p.paymentId] = p;
    });
    return map;
  }, [payments]);

  const getSourcePayment = (record) => paymentByPaymentId[record.paymentId];
  const getSplits = (record) => resolveSplits(record, getSourcePayment(record));

  // Table columns
  const columns = [
    {
      title: 'Voucher ID',
      dataIndex: 'voucherId',
      key: 'voucherId',
      width: 130,
      render: (id) => <Text strong>{id}</Text>,
      sorter: (a, b) => a.voucherId.localeCompare(b.voucherId),
    },
    {
      title: 'Payment ID',
      dataIndex: 'paymentId',
      key: 'paymentId',
      width: 130,
      ellipsis: true,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Text strong style={{ color: '#3f8600' }}>
          {formatWholeAmount(amount)}
        </Text>
      ),
      width: 120,
      sorter: (a, b) => (a.amount || 0) - (b.amount || 0),
    },
    {
      title: 'UTR / Ref.',
      key: 'utr',
      width: 240,
      render: (_, record) => (
        <SplitReferenceCell
          record={record}
          splits={getSplits(record)}
          sourcePayment={getSourcePayment(record)}
          expectedTotal={record.amount}
        />
      ),
    },
    {
      title: 'Recipient',
      dataIndex: 'recipientName',
      key: 'recipientName',
      ellipsis: true,
      width: 140,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        let color = 'default';
        let text = status;
        
        if (status === 'ISSUED') {
          color = 'green';
        } else if (status === 'REGENERATED') {
          color = 'orange';
          text = `${status} (${record.regenerationHistory?.length || 0}x)`;
        } else if (status === 'VOIDED') {
          color = 'red';
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
      width: 120,
    },
    {
      title: 'Issued At',
      dataIndex: 'issuedAt',
      key: 'issuedAt',
      render: (date) => date ? dayjs(date).format('DD MMM YYYY') : '-',
      width: 120,
      sorter: (a, b) => new Date(a.issuedAt) - new Date(b.issuedAt),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewVoucher(record.voucherId)}
            />
          </Tooltip>
          
          <Tooltip title="Print PDF">
            <Button
              type="text"
              size="small"
              icon={<PrinterOutlined />}
              onClick={() => handleQuickPrint(record.pdfUrl)}
              disabled={!record.pdfUrl}
            />
          </Tooltip>

          <Tooltip title="Download PDF">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleQuickDownload(record.pdfUrl, record.voucherId)}
              disabled={!record.pdfUrl}
            />
          </Tooltip>

          <Tooltip title="Regenerate PDF">
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              loading={regeneratingVoucherId === record.voucherId}
              onClick={() => handleRegenerateVoucher(record.voucherId)}
            />
          </Tooltip>

          <Popconfirm
            title="Delete this voucher?"
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true, loading: deletingVoucherId === record.voucherId }}
            cancelText="Cancel"
            onConfirm={() => handleDeleteVoucher(record.voucherId)}
          >
            <Tooltip title="Delete Voucher">
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deletingVoucherId === record.voucherId}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      
      {/* Header */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Title level={5} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: 8 }} />
              Settlement Payment Vouchers
              {vouchers.length > 0 && <Text type="secondary"> ({vouchers.length})</Text>}
            </Title>
            <Text type="secondary">Vouchers generated for settlement payments to customer</Text>
          </div>
          
          <Space>
            {vouchers.length > 0 && (
              <Text type="secondary">
                Total: {formatWholeAmount(vouchers.reduce((sum, v) => sum + (v.amount || 0), 0))}
              </Text>
            )}
            {payments.length > 0 && getMissingVouchers().length > 0 && (
              <Tooltip title={`${getMissingVouchers().length} settlement payment(s) without voucher(s)`}>
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  loading={generatingVouchers}
                  onClick={handleGenerateMissingVouchers}
                >
                  Generate Voucher ({getMissingVouchers().length})
                </Button>
              </Tooltip>
            )}
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={loadVouchers}
              loading={loading}
              title="Refresh vouchers"
            />
          </Space>
        </Space>
      </Card>

      {/* Vouchers Table */}
      <Spin spinning={loading}>
        <Table
          dataSource={vouchers.map((v) => ({ ...v, key: v._id }))}
          columns={columns}
          pagination={vouchers.length > 10 ? { 
            pageSize: 10, 
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} vouchers`,
          } : false}
          locale={{
            emptyText: <Empty description="No settlement payment vouchers yet" />,
          }}
          scroll={{ x: 'max-content' }}
          size="small"
          bordered
        />
      </Spin>

      {/* Voucher Details Modal */}
      {detailsModalOpen && selectedVoucherId && (
        <VoucherDetailsModal
          voucherId={selectedVoucherId}
          open={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedVoucherId(null);
          }}
          onRegenerateSuccess={() => {
            loadVouchers();
            onVoucherUpdate?.();
          }}
        />
      )}
    </>
  );
};

export default SettlementPaymentVoucherTable;
